import React, { useState, useEffect } from "react";
import { Separator } from "components/separator/Separator";
import { Text, Flex, Button, Checkbox, CheckboxGroup, TextField, Popover, IconButton } from "@radix-ui/themes";
import { Cross1Icon, MagnifyingGlassIcon, ChevronDownIcon } from "@radix-ui/react-icons";

export function BrandMultiSelect({ width = null, noShowBorder, options, onValueChange, defaultValue, disabled }) {
  const [selectedValues, setSelectedValues] = useState(defaultValue || []);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [displayText, setDisplayText] = useState("All brands");
  const [topCheckBoxBool, setTopCheckBoxBool] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize selected values when defaultValue changes
  useEffect(() => {
    if (defaultValue) {
      setSelectedValues(defaultValue);
      updateDisplayAndCheckboxes(defaultValue);
    }
  }, [defaultValue]);

  const updateDisplayAndCheckboxes = (newSelectedValues) => {
    setDisplayText(
      newSelectedValues.length === 0 ? "Select brands" : newSelectedValues.length === options.length ? "All brands" : `${newSelectedValues.length} brand${newSelectedValues.length > 1 ? "s" : ""}`
    );
    setTopCheckBoxBool(newSelectedValues.length === options.length);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onValueChange([]);
    updateDisplayAndCheckboxes([]);
  };

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      handleClear();
    } else {
      const allValues = options.map((option) => option.value);
      setSelectedValues(allValues);
      onValueChange(allValues);
      updateDisplayAndCheckboxes(allValues);
    }
  };

  const clearSearchQuery = () => {
    setSearchQuery("");
  };

  const filteredOptions = options
    .filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Popover.Trigger asChild>
        <Button
          style={{
            cursor: `${disabled ? "not-allowed" : "default"}`,
            width: width,
            marginLeft: noShowBorder ? "3px" : null,
          }}
          size="2"
          variant={noShowBorder ? "ghost" : "soft"}
          color={selectedValues.length === 0 ? "red" : "gray"}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          disabled={disabled}
        >
          <Text color="gray" weight="medium">
            {displayText}
          </Text>
          {width ? <Flex flexGrow="1" /> : null}
          <ChevronDownIcon style={{ color: "var(--gray-11)" }} />
        </Button>
      </Popover.Trigger>

      <Popover.Content maxHeight="500px" style={{ display: "flex", width: "296px", flexDirection: "column",
          alignItems: "start", padding: "0" }}>
        <Flex className="slot-container" style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Search Row */}
          <Flex
            className="search-row"
            style={{
              alignSelf: "stretch",
              padding: "var(--space-2) 8px var(--space-2) 20px",
            }}
            justify="start"
            align="center"
            gap="2"
          >
            <Checkbox size="2" variant="surface" checked={topCheckBoxBool} onCheckedChange={toggleAll} />
            <TextField.Root style={{ flexGrow: "1" }} size="2" variant="soft" color="gray" placeholder="Search" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)}>
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>

              <TextField.Slot>
                <IconButton size="1" variant="ghost" color="gray" onClick={clearSearchQuery} >
                  <Cross1Icon height="16" width="16" />
                </IconButton>
              </TextField.Slot>
            </TextField.Root>
          </Flex>
          <Separator color="gray" size="1" />
          {/* Options List */}
          <Flex className="brand-list-container" style={{ alignSelf: "stretch" }} display="flex" p="2" direction="column" justify="center" align="start" gap="2">
            <CheckboxGroup.Root
              size="2"
              variant="surface"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                alignSelf: "stretch",
                gap: "0px",
              }}
              value={selectedValues}
              onValueChange={(newValues) => {
                setSelectedValues(newValues);
                onValueChange(newValues);
                updateDisplayAndCheckboxes(newValues);
              }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Flex
                    key={option.value}
                    display="flex"
                    style={{
                      alignSelf: "stretch",
                      maxWidth: "100%",
                      height: "var(--space-6)",
                      padding: "0 var(--space-3)",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <CheckboxGroup.Item value={option.value} />
                    <span
                      style={{
                        fontSize: "var(--font-size-2)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "280px",
                        display: "inline-block",
                        lineHeight: "var(--line-height-2)",
                        color: "var(--gray-12)",
                      }}
                    >
                      {option.label}
                    </span>
                  </Flex>
                ))
              ) : (
                <Text px="3">No Brands Found</Text>
              )}
            </CheckboxGroup.Root>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
